import { put, select, call, takeLatest } from 'redux-saga/effects'
import { cloneDeep } from 'lodash'
import { IPcr } from '../../pcr/store/pcr.types'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { getPulsaraSelector } from '../selectors'
import { IPulsaraState } from '../store/pulsara.types'
import { buildResolvedPatient, calculateDefaultSelections, getPatientConflicts } from '../utils/pulsaraConflict.util'
import {
  setEmsChartsData,
  setImportSelections,
  setPatientSelections,
  fetchPulsaraAttachments,
} from '../store/pulsara.actions'
import { PATIENT_MATCH_SUB_STEP_IDS } from '../constants'
import { setCurrentSubStep } from '../store/pulsara.actions'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { getPcrIdSelector } from '../../home/selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { initialPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.reducer'
import { PulsaraConstants } from '../store/pulsara.constants'
import { initialSituationState } from '../../ccHpi/store/ccHpi.reducers'
import { initialPatientInformationState } from '../../patientInformation/store/patientInformation.reducers'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { getPatientSearchState } from '../../patientSearch/selectors'
import { IPatientSearchState } from '../../patientSearch/store/patientSearch.types'
import { IEmsChartsData } from '../store/pulsara.types'
import { resetLoader } from '../../../components/loader/store/loader.actions'

export function* initializePatientConflictSelections(emsChartsDataInput: IEmsChartsData) {
  try {
    const pulsaraState: IPulsaraState = yield select(getPulsaraSelector)
    const { pulsaraDetails, selectedPulsaraId } = pulsaraState
    const { FORCE_PT_SEARCH } = yield select(getServiceConfigSelector)

    const conflictFields = getPatientConflicts(pulsaraDetails, emsChartsDataInput)
    const defaultSelections = calculateDefaultSelections(conflictFields, FORCE_PT_SEARCH)

    const resolvedPatient = buildResolvedPatient(pulsaraDetails, emsChartsDataInput, defaultSelections)

    yield put(setPatientSelections(defaultSelections))
    yield put(
      setImportSelections({
        ptid: emsChartsDataInput.ptid ?? null,
        patient: {
          patientDemographics: {
            ...emsChartsDataInput.patient?.patientDemographics,
            ...resolvedPatient.patientDemographics,
          },
          patientImmunizations: emsChartsDataInput.patient?.patientImmunizations,
          patientAllergies: emsChartsDataInput.patient?.patientAllergies,
          patientMedications: emsChartsDataInput.patient?.patientMedications,
          patientMedicalHistory: emsChartsDataInput.patient?.patientMedicalHistory,
          patientInformation: emsChartsDataInput.patient?.patientInformation,
        },
        situation: {
          ...emsChartsDataInput.situation,
          ...resolvedPatient.situation,
        },
        billing: emsChartsDataInput.billing,
        selectedVitals: [],
        selectedLabs: [],
        selectedAttachments: [],
      })
    )

    if (selectedPulsaraId && !pulsaraDetails?.attachmentList?.length) {
      yield put(fetchPulsaraAttachments())
    }
  } catch (e) {
    yield put(throwPcrException(`initializePatientConflictSelections() error: ${e}`))
  }
}

export function* initializePatientConflictWithProcessedData(processedPCR: IPcr) {
  try {
    const processedPCRClone = cloneDeep(processedPCR)
    const situation = cloneDeep(processedPCRClone.situation || initialSituationState)
    const patientInformationData = {
      ...initialPatientInformationState,
      searchImport: true,
      newPatient: false,
      hasPatient: true,
    }
    const emsChartsDataForConflict: IEmsChartsData = {
      ptid: processedPCRClone.ptid || '',
      patient: {
        ...processedPCRClone.patient,
        patientInformation: patientInformationData,
      },
      billing: processedPCRClone.billing,
      situation: { ...situation, complaint: situation.complaint || '' },
    }

    yield put(setEmsChartsData(emsChartsDataForConflict))

    yield call(initializePatientConflictSelections, emsChartsDataForConflict)
    yield put(resetLoader())
    yield put(setCurrentSubStep(PATIENT_MATCH_SUB_STEP_IDS.PATIENT_CONFLICT))
  } catch (e) {
    yield put(throwPcrException(`initializePatientConflictWithProcessedData() error: ${e}`))
  }
}

export function* initializePatientConflictFromPCR() {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pcrID: string = yield select(getPcrIdSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    const { FORCE_PT_SEARCH } = yield select(getServiceConfigSelector)

    const patientDemographicsData = cloneDeep(pcrDoc?.patient?.patientDemographics || initialPatientDemographicsState)
    const situation = cloneDeep(pcrDoc?.situation || initialSituationState)
    const isNewPatient = FORCE_PT_SEARCH === 1 && !pcrDoc.ptid //  we only call this action either if FORCE_PT_SEARCH is 0 or on New patient button click
    let patientInformationData
    let emsChartsDataOverrides = {}

    if (isNewPatient) {
      const patientSearchState: IPatientSearchState = yield select(getPatientSearchState)
      const { firstName, lastName, dOB } = patientSearchState
      patientDemographicsData.firstName = firstName || ''
      patientDemographicsData.lastName = lastName || ''
      patientDemographicsData.dOB = dOB || null
      patientInformationData = {
        ...initialPatientInformationState,
        searchImport: false,
        newPatient: true,
        hasPatient: true,
      }
      emsChartsDataOverrides = {
        patientInformation: patientInformationData,
      }
    }

    const emsChartsDataFromPCR: IEmsChartsData = {
      ptid: pcrDoc?.ptid || '',
      patient: {
        patientDemographics: {
          ...patientDemographicsData,
          firstName: patientDemographicsData.firstName || '',
          lastName: patientDemographicsData.lastName || '',
          dOB: patientDemographicsData.dOB || null,
          age: patientDemographicsData.age || null,
          ageUnit: patientDemographicsData.ageUnit || null,
          weight: patientDemographicsData.weight || null,
          weightUnits: patientDemographicsData.weightUnits || null,
          height: patientDemographicsData.height || null,
          heightUnits: patientDemographicsData.heightUnits || null,
        },
        patientInformation: pcrDoc?.patient?.patientInformation || cloneDeep(initialPatientInformationState),
        patientImmunizations: pcrDoc?.patient?.patientImmunizations,
        patientAllergies: pcrDoc?.patient?.patientAllergies,
        patientMedications: pcrDoc?.patient?.patientMedications,
        patientMedicalHistory: pcrDoc?.patient?.patientMedicalHistory,
        ...emsChartsDataOverrides,
      },
      situation: {
        ...situation,
        complaint: situation.complaint || '',
      },
      billing: pcrDoc?.billing,
    }

    yield put(setEmsChartsData(emsChartsDataFromPCR))

    yield call(initializePatientConflictSelections, emsChartsDataFromPCR)
    yield put(setCurrentSubStep(PATIENT_MATCH_SUB_STEP_IDS.PATIENT_CONFLICT))
  } catch (e) {
    yield put(throwPcrException(`initializePatientConflictFromPCR() error: ${e}`))
  }
}

export function* populateEmsChartsFromPCRSaga() {
  yield takeLatest(PulsaraConstants.POPULATE_EMS_CHARTS_FROM_PCR, performWithMutexHOG, initializePatientConflictFromPCR)
}
